// src/hooks/useData.js
import { useSelector, useDispatch } from "react-redux";
import { useCallback, useEffect, useMemo } from "react";
import {
  selectGeoJSON,
  selectScoredData,
  selectGeographies,
  selectGeographiesById,
  selectTopGeographies,
  selectTierCounts,
  selectTotalCount,
  selectScoredCount,
  selectScoreStats,
  selectDataLoading,
  selectDataError,
  selectLoadedMapId,
  selectActiveCompetitorData,
  selectActiveCompetitors,
  selectCompetitorsLoading,
  selectCompetitorsError,
  selectActiveOpportunityData,
  selectActiveOpportunities,
  selectOpportunitiesLoading,
  selectOpportunitiesError,
  fetchMapData,
  fetchCompetitorData,
  fetchOpportunityData,
  clearData,
  setLoadedMapId,
} from "../store/dataSlice";
import {
  selectActiveMapId,
  selectActiveDataPaths,
  selectActiveDisplayConfig,
} from "../store/configSlice";
import {
  selectCompetitorsVisible,
  selectOpportunitiesVisible,
} from "../store/mapSlice";
import { useDisplayConfig, useTierConfig } from "./useConfig";

/**
 * Hook to load and access map data
 * Automatically loads data when active map changes
 */
export const useMapData = () => {
  const dispatch = useDispatch();

  const activeMapId = useSelector(selectActiveMapId);
  const loadedMapId = useSelector(selectLoadedMapId);
  const dataPaths = useSelector(selectActiveDataPaths);

  const geoJSON = useSelector(selectGeoJSON);
  const scoredData = useSelector(selectScoredData);
  const loading = useSelector(selectDataLoading);
  const error = useSelector(selectDataError);

  // Load data when active map changes
  useEffect(() => {
    if (activeMapId && activeMapId !== loadedMapId && dataPaths) {
      console.log(`📊 Loading data for map: ${activeMapId}`);

      dispatch(fetchMapData())
        .unwrap()
        .then(() => {
          dispatch(setLoadedMapId(activeMapId));
          console.log(`✅ Map data loaded: ${activeMapId}`);
        })
        .catch((err) => {
          console.error("Failed to load map data:", err);
        });
    }
  }, [activeMapId, loadedMapId, dataPaths, dispatch]);

  const reload = useCallback(() => {
    if (dataPaths) {
      dispatch(clearData());
      dispatch(fetchMapData());
    }
  }, [dataPaths, dispatch]);

  return {
    geoJSON,
    scoredData,
    loading,
    error,
    reload,
    isLoaded:
      loadedMapId === activeMapId && !loading && !!geoJSON && !!scoredData,
  };
};

/**
 * Hook to access geography data
 */
export const useGeographies = () => {
  const geographies = useSelector(selectGeographies);
  const geographiesById = useSelector(selectGeographiesById);
  const totalCount = useSelector(selectTotalCount);
  const scoredCount = useSelector(selectScoredCount);
  const scoreStats = useSelector(selectScoreStats);

  const getGeography = useCallback(
    (id) => {
      return geographiesById[id] || null;
    },
    [geographiesById],
  );

  return {
    geographies,
    geographiesById,
    totalCount,
    scoredCount,
    scoreStats,
    getGeography,
  };
};

/**
 * Hook to access top geographies (by rank)
 */
export const useTopGeographies = (n = 10) => {
  const geographies = useSelector(selectGeographies);

  const topGeographies = useMemo(() => {
    return [...geographies]
      .sort((a, b) => (a.rank || 9999) - (b.rank || 9999))
      .slice(0, n);
  }, [geographies, n]);

  return topGeographies;
};

/**
 * Hook to access tier counts
 */
export const useTierCounts = () => {
  const tierCounts = useSelector(selectTierCounts);
  const totalCount = useSelector(selectTotalCount);
  const { tierOrder } = useTierConfig();

  // Add counts to tier order for easy iteration
  const tiersWithCounts = useMemo(() => {
    return tierOrder.map((tier) => ({
      ...tier,
      count:
        tierCounts[tier.normalizedKey] ||
        tierCounts[tier.label?.toLowerCase()] ||
        0,
    }));
  }, [tierOrder, tierCounts]);

  return {
    tierCounts,
    totalCount,
    tiersWithCounts,
    noDataCount: tierCounts["no data"] || 0,
  };
};

/**
 * Hook to access a specific geography's details
 */
export const useGeographyDetails = (geographyId) => {
  const geographiesById = useSelector(selectGeographiesById);
  const totalCount = useSelector(selectTotalCount);
  const { getTierColor, getTierLabel } = useTierConfig();
  const { maxScore } = useDisplayConfig();

  const geography = geographiesById[geographyId] || null;

  const details = useMemo(() => {
    if (!geography) return null;

    const tier = geography.tier;

    return {
      id: geography.geoId,
      label: geography.city || geography.geoId,
      secondaryLabel: geography.zcta || geography.geoId,
      rank: geography.rank,
      totalCount,
      score: geography.score,
      maxScore,
      tier,
      tierColor: getTierColor(tier),
      tierLabel: getTierLabel(tier),
      breakdown: geography.breakdown || [],
      coordinates: geography.coordinates || null,
      raw: geography,
    };
  }, [geography, getTierColor, getTierLabel, maxScore, totalCount]);

  return details;
};

/**
 * Hook for GeoJSON to scored data matching
 * This is the key hook that links polygon features to scored data
 */
export const useGeoJsonMatcher = () => {
  const geographiesById = useSelector(selectGeographiesById);
  const displayConfig = useSelector(selectActiveDisplayConfig);

  const geoJsonIdField = displayConfig?.geoJsonIdField || "GEOID20";
  const geoJsonIdPrefix = displayConfig?.geoJsonIdPrefix || null;

  /**
   * Extract the geography ID from a GeoJSON feature
   * Handles the mapping between GeoJSON property names and scored data IDs
   */
  const getGeoIdFromFeature = useCallback(
    (feature) => {
      if (!feature?.properties) return null;

      // Get the ID from the GeoJSON properties using configured field name
      const rawId = feature.properties[geoJsonIdField];
      if (!rawId) {
        // Try common alternatives
        const alternatives = ["GEOID20", "GEOID", "ZCTA5CE20", "geoId"];
        for (const alt of alternatives) {
          if (feature.properties[alt]) {
            return feature.properties[alt];
          }
        }
        return null;
      }

      // If there's a prefix in the scored data but not in GeoJSON, handle it
      if (geoJsonIdPrefix) {
        // Scored data uses prefixed IDs, GeoJSON doesn't
        const prefixedId = `${geoJsonIdPrefix}${rawId}`;
        if (geographiesById[prefixedId]) {
          return prefixedId;
        }
      }

      // Direct match
      return rawId;
    },
    [geoJsonIdField, geoJsonIdPrefix, geographiesById],
  );

  /**
   * Get scored geography data for a GeoJSON feature
   */
  const getGeographyForFeature = useCallback(
    (feature) => {
      const geoId = getGeoIdFromFeature(feature);
      if (!geoId) return null;

      // Try direct match
      if (geographiesById[geoId]) {
        return geographiesById[geoId];
      }

      // Try without leading zeros (some datasets have this inconsistency)
      const strippedId = geoId.replace(/^0+/, "");
      if (geographiesById[strippedId]) {
        return geographiesById[strippedId];
      }

      // Try with leading zeros padded to 5 digits (ZCTA format)
      const paddedId = geoId.padStart(5, "0");
      if (geographiesById[paddedId]) {
        return geographiesById[paddedId];
      }

      return null;
    },
    [getGeoIdFromFeature, geographiesById],
  );

  return {
    geoJsonIdField,
    geoJsonIdPrefix,
    getGeoIdFromFeature,
    getGeographyForFeature,
  };
};

/**
 * Hook to lazily load and access competitor data for the active map.
 * Fetches competitor data on first toggle activation; caches by path.
 */
export const useCompetitorData = () => {
  const dispatch = useDispatch();
  const visible = useSelector(selectCompetitorsVisible);
  const dataPaths = useSelector(selectActiveDataPaths);
  const competitorData = useSelector(selectActiveCompetitorData);
  const competitors = useSelector(selectActiveCompetitors);
  const loading = useSelector(selectCompetitorsLoading);
  const error = useSelector(selectCompetitorsError);

  // Lazy load: fetch when first toggled on and data not yet loaded
  useEffect(() => {
    if (
      visible &&
      dataPaths?.competitors &&
      competitorData === null &&
      !loading &&
      !error
    ) {
      console.log(`📍 Loading competitor data: ${dataPaths.competitors}`);
      dispatch(fetchCompetitorData());
    }
  }, [
    visible,
    dataPaths?.competitors,
    competitorData,
    loading,
    error,
    dispatch,
  ]);

  return {
    competitors,
    competitorData,
    loading,
    error,
    visible,
    hasCompetitorsPath: !!dataPaths?.competitors,
  };
};

/**
 * Hook to lazily load and access opportunity data for the active map.
 * Fetches opportunity data on first toggle activation; caches by path.
 */
export const useOpportunityData = () => {
  const dispatch = useDispatch();
  const visible = useSelector(selectOpportunitiesVisible);
  const dataPaths = useSelector(selectActiveDataPaths);
  const opportunityData = useSelector(selectActiveOpportunityData);
  const opportunities = useSelector(selectActiveOpportunities);
  const loading = useSelector(selectOpportunitiesLoading);
  const error = useSelector(selectOpportunitiesError);

  // Lazy load: fetch when first toggled on and data not yet loaded
  useEffect(() => {
    if (
      visible &&
      dataPaths?.opportunities &&
      opportunityData === null &&
      !loading &&
      !error
    ) {
      console.log(`📍 Loading opportunity data: ${dataPaths.opportunities}`);
      dispatch(fetchOpportunityData());
    }
  }, [
    visible,
    dataPaths?.opportunities,
    opportunityData,
    loading,
    error,
    dispatch,
  ]);

  return {
    opportunities,
    opportunityData,
    loading,
    error,
    visible,
    hasOpportunitiesPath: !!dataPaths?.opportunities,
  };
};

/**
 * Hook for searching geographies
 */
export const useGeographySearch = () => {
  const geographies = useSelector(selectGeographies);
  const features = useSelector((state) => state.config.clientConfig?.features);

  const searchableFields = features?.search?.searchableFields || [
    "city",
    "zcta",
    "geoId",
    "county",
  ];

  const search = useCallback(
    (query) => {
      if (!query || query.length < 2) return [];

      const lowerQuery = query.toLowerCase().trim();

      return geographies
        .filter((geo) => {
          return searchableFields.some((field) => {
            const value = geo[field];
            if (!value) return false;

            if (Array.isArray(value)) {
              return value.some((v) =>
                String(v).toLowerCase().includes(lowerQuery),
              );
            }

            return String(value).toLowerCase().includes(lowerQuery);
          });
        })
        .sort((a, b) => (a.rank || 9999) - (b.rank || 9999))
        .slice(0, 20);
    },
    [geographies, searchableFields],
  );

  return { search };
};
